import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Container, Spinner } from "react-bootstrap";
import TermsModal from "./Main/Terms";  // Import Terms modal
import "./Register.css"; // Import the CSS file


const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    walletAddress: '',
    referralCode: '',
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [termsChecked, setTermsChecked] = useState(false); // State for checkbox
  const [showModal, setShowModal] = useState(false); // State to control modal visibility

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCheckboxChange = () => {
    if (!termsChecked) {
      setShowModal(true); // Open modal if checkbox is not checked
    }
  };

  const handleAgreeToTerms = () => {
    setTermsChecked(true); // Set the checkbox to checked once the user agrees
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.walletAddress) newErrors.walletAddress = "Wallet Address is required.";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format.";
    if (!formData.password) newErrors.password = "Password is required.";
    if (formData.password.length < 8) newErrors.password = "Password must be at least 8 characters.";
    if (!termsChecked) newErrors.termsChecked = "You must agree to the terms and conditions.";
    // we made this optional
    // if (!formData.referralCode) newErrors.referralCode = "Referral Code is required to register.";
    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (formData.password !== confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match!" });
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      const response = await fetch('https://sharedvision-partners.online/v1/API/register_us.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
     
      setLoading(false);

      if (result.success) {
        setSuccessMessage('User registered successfully!');
        resetForm();
        startCountdown();
      } else {
        if (result.errors) {
          setErrors(result.errors);
        } else if (result.message) {
          setErrors({ general: result.message });
        } else {
          setErrors({ general: "An unknown error occurred. Please try again later." });
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);
      setErrors({ general: "An error occurred while submitting the form. Please check your network connection or try again later." });
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      walletAddress: '',
      referralCode: '',
    });
    setConfirmPassword('');
    setErrors({});
    setTermsChecked(false); // Reset the checkbox
  };

  const startCountdown = () => {
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(countdownInterval);
          navigate('/'); // Redirect to login page
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (successMessage) {
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(countdownInterval);
            navigate('/');
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(countdownInterval);
    }
  }, [successMessage, navigate]);

  const formContainerStyle = {
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    backgroundColor: "transparent",
    width: "100%",
  };

  const inputStyle = {
    height: "40px",
    borderRadius: "8px",
    padding: "10px",
    fontSize: "14px",
  };

  const buttonStyle = {
    backgroundColor: "#007BFF",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 0",
    fontSize: "16px",
    width: "100%",
  };

  const headerStyle = {
    color: "#333",
    fontSize: "24px",
    fontWeight: "bold",
  };

  return (
    <div className="row min-vh-100">
      {/* Left Column for the Registration Form */}
      <div className="col-md-6 d-flex justify-content-center align-items-center p-4">
        <Container className="d-flex justify-content-center align-items-center w-100" style={{ maxWidth: '500px' }}>
          <div className="w-100" style={formContainerStyle}>
            {/* Logo */}
            <div className="text-center mb-4">
              <img src="/logo.png" alt="Logo" style={{ width: "100px" }} />
            </div>

            <h2 className="mb-3" style={headerStyle}>Sign up for an account</h2>

            {/* Success message with countdown */}
            {successMessage && (
              <div className="success">
                <p>{successMessage}</p>
                <p>You will be redirected to the login page in {countdown} seconds...</p>
              </div>
            )}

            {/* General error message */}
            {errors.general && <div className="error">{errors.general}</div>}

            <Form onSubmit={handleSubmit}>
              {/* Email Input */}
              <Form.Group controlId="formEmail" className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter Your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={inputStyle}
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </Form.Group>

              {/* Password and Confirm Password Input */}
              <div className="row">
                <Form.Group controlId="formPassword" className="col-md-6">
                  <Form.Label>Password</Form.Label>
                  <div className="password-wrapper">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={handleInputChange}
                      style={inputStyle}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="toggle-btn"
                    >
                      {showPassword ? "Hide" : "Show"}
                    </Button>
                  </div>
                  {errors.password && <span className="error">{errors.password}</span>}
                </Form.Group>

                <Form.Group controlId="formConfirmPassword" className="col-md-6 mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <div className="password-wrapper">
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={inputStyle}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="toggle-btn"
                    >
                      {showConfirmPassword ? "Hide" : "Show"}
                    </Button>
                  </div>
                  {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
                </Form.Group>
              </div>

              {/* Referral Code Input */}
              <Form.Group controlId="formReferralCode" className="mb-3">
                <Form.Label>Referral Code</Form.Label>
                <Form.Control
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  placeholder="You and your referrer will get $5 bonus each"
                  style={inputStyle}
                />
                {errors.referralCode && <span className="error">{errors.referralCode}</span>}
              </Form.Group>

              {/* Wallet Address Input */}
              <Form.Group controlId="formWalletAddress" className="mb-3">
                <Form.Label>Wallet Address</Form.Label>
                <Form.Control
                  type="text"
                  name="walletAddress"
                  value={formData.walletAddress}
                  onChange={handleInputChange}
                  placeholder="Enter your wallet address to receive funds"
                  style={inputStyle}
                />
                {errors.walletAddress && <span className="error">{errors.walletAddress}</span>}
              </Form.Group>

              {/* Terms and Conditions Checkbox */}
              <Form.Group controlId="formTerms" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="I agree to the Terms and Conditions"
                  checked={termsChecked}
                  onChange={handleCheckboxChange}
                />
                {errors.termsChecked && <span className="error">{errors.termsChecked}</span>}
              </Form.Group>

              {/* Submit Button */}
              <Button type="submit" disabled={loading} variant="primary" style={buttonStyle}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Register'}
              </Button>
            </Form>

            {/* Link to login page */}
            <div className="mt-3 text-center">
              Already have an account? <a href="/">Login here</a>
              <p className="text-muted">Check your Inbox or Spam folder for a welcome message</p>
            </div>
          </div>
        </Container>
      </div>

    
 {/* Right Column with Welcome Message */}
 <div className="col-md-6 bg-light d-flex justify-content-center align-items-center">
        <div className="p-4 text-center">
          <h3>Welcome to SharedVision Partners!</h3>
          <div className="content">
          <div className="content">
            <p align="left">Dear Valued Investment Partners, We are thrilled to have you on board with us. SharedVision Partners was created with a singular purpose: to bring together like-minded individuals who share a vision for investing and making a meaningful impact with their funds. We believe in the power of collaboration, where each partner contributes to a common goal and reaps the rewards together.</p>
            <p align="left">Our platform is designed to connect you to great investment opportunities that are carefully curated to ensure your funds are put to good use, yielding both profits and benefits. We understand that your hard-earned capital is precious, and we promise to put it in the hands of worthy ventures, ensuring a great return on your investment.</p>
          </div>

           {/* Show More Button to toggle visibility */}
          <button
            type="button"
            onClick={() => document.querySelector('.extra-content').classList.toggle('show')}
            className="btn btn-primary mb-3"
          >
            Show More
          </button>
          
          <div className="extra-content">
            <p align="left">As the name "SharedVision" implies, we see you as our true partners. Here, you are not just an investor – you are a part of a collective effort that spans across the globe, joining forces with others who share the same vision of success. Together, we create opportunities that grow and prosper, benefiting all of us. Thank you for choosing to partner with us. We are excited about the journey ahead and the exceptional opportunities we will share along the way.</p>
            <p>Warm regards, The SharedVision Partners Team.</p>
          </div>
          </div>
        </div>
      </div>





      {/* Terms and Conditions Modal */}
      <TermsModal show={showModal} handleClose={() => setShowModal(false)} handleAgree={handleAgreeToTerms} />
    </div>
  );
};

export default Register;
